import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService, HttpResponse, HttpStatusEnum } from '@core/api';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes, ToasterHelper } from '@shared/helpers';
import {
    AddInformedUserPayload,
    EstimateAmountBody,
    EstimatedAmount,
    EstimatedAmountDTO,
    InitialPrepareStepThree,
    InitialPrepareStepThreeDTO,
    PrepareStepThreeByUser,
    PrepareStepThreeByUserDTO,
    PrimaryBillingAddress,
    RefreshBillingAddressDTO,
} from '@shared/interfaces';
import { MessageService } from 'primeng/api';
import {
    DraftOrderDTO,
    ExcelOrderDTO,
    ExcelOrderData,
    Order,
    PrepareOrderSummaryPayload,
    PrepareStepTwo,
    PrepareStepTwoDTO,
    PrimerManagementData,
    Reaction,
    SaveDraftPayload,
    StepTwoSampleData,
    StepTwoSampleDataDTO,
    SubmitOrderDTO,
    SubmitOrderPayload,
    SubmitOrderResponse,
    ValidateNumberOfSamples,
    ValidateNumberOfSamplesDTO,
    ValidateNumberOfSamplesPayload,
    ValidateReactionDetails,
    ValidateReactionDetailsDTO,
    ValidateReactionDetailsPayload,
} from '../interfaces';
import { OrderSetupMapper } from './order-setup.mapper';

@Injectable()
export class OrderSetupApi {
    private readonly resourceUrl = 'order-setup';
    constructor(
        private apiService: ApiService,
        private messageService: MessageService,
        private router: Router,
        private translateService: TranslateService
    ) {}

    prepareDataForReactionDetails(): Promise<HttpResponse<PrepareStepTwo>> {
        return new Promise<HttpResponse<PrepareStepTwo>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/prepare-step-2`).subscribe({
                next: (res: PrepareStepTwoDTO) => {
                    resolve(OrderSetupMapper.mapPrepareStepTwoDTOtoModel(res));
                },
                error: async () => {
                    await this.router.navigateByUrl(AppRoutes.routes().ERROR);
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    validateNumberOfReactions(payload: PrimerManagementData) {
        const body: ValidateNumberOfSamplesPayload = {
            recentCustomPrimers: payload.customPrimers,
            recentShippedPrimers: payload.shippedPrimers,
        };
        return new Promise<HttpResponse<ValidateNumberOfSamples>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/validate-step-1`, body).subscribe({
                next: (res: ValidateNumberOfSamplesDTO) => {
                    resolve(OrderSetupMapper.mapValidateNumberOfReactionsDTOtoModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    validateReactionDetails(reactions: Reaction[]) {
        const payload: ValidateReactionDetailsPayload = {
            step2: {
                samples: reactions.map(reaction => {
                    return {
                        barcode: reaction.barcode,
                        customPrimerNames: reaction.customPrimers,
                        dnaType: reaction.dnaType,
                        name: reaction.sampleName,
                        recentCustomPrimerNames: reaction.recentCustomPrimers,
                        recentShippedPrimerNames: reaction.shippedPrimers,
                        size: reaction.size,
                        standardPrimerNames: reaction.standardPrimers,
                        locked: reaction.isUsedSample,
                    };
                }),
            },
        };
        return new Promise<HttpResponse<ValidateReactionDetails>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/validate-step-2`, payload).subscribe({
                next: (res: ValidateReactionDetailsDTO) => {
                    resolve(OrderSetupMapper.mapValidateReactionDetailsDTOtoModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    prepareInitialDataForOrderSummary(order: Order) {
        const body: PrepareOrderSummaryPayload = OrderSetupMapper.mapOrderForPrepareOrderSummary(order);
        return new Promise<HttpResponse<InitialPrepareStepThree>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/prepare-step-3-initial`, body).subscribe({
                next: (res: InitialPrepareStepThreeDTO) => {
                    resolve(OrderSetupMapper.mapInitialPrepareStepThreeDTOtoModel(res));
                },
                error: async () => {
                    await this.router.navigateByUrl(AppRoutes.routes().ERROR);
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    prepareDataForOrderSummaryByUser(userId: number) {
        return new Promise<HttpResponse<PrepareStepThreeByUser>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/prepare-step-3-by-user`, { userId: userId }).subscribe({
                next: (res: PrepareStepThreeByUserDTO) => {
                    resolve(OrderSetupMapper.mapPrepareStepThreeByUserDTOtoModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    refreshBillingAddress() {
        return new Promise<HttpResponse<PrimaryBillingAddress | null>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/default-billing-address`).subscribe({
                next: (res: RefreshBillingAddressDTO) => {
                    resolve(OrderSetupMapper.mapRefreshBillingAddressDTOtoModel(res));
                },
                error: async () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    submitOrder(payload: SubmitOrderPayload) {
        return new Promise<HttpResponse<SubmitOrderResponse>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/submit-order`, payload).subscribe({
                next: (res: SubmitOrderDTO) => {
                    resolve(OrderSetupMapper.mapSubmitOrderDTOToModel(res));
                },
                error: (err: { error: { error: string; message: string; timestamp: string } }) => {
                    if (err.error.error === 'ExternalServiceException') {
                        resolve({ data: null, status: HttpStatusEnum.LIMS_ISSUES });
                    } else {
                        resolve({ data: null, status: HttpStatusEnum.ERROR });
                    }
                },
            });
        });
    }

    getOrderEstimate(reactions: Reaction[], pickupCourierId: number | null, hasShipping: boolean) {
        const body: EstimateAmountBody = {
            samples: reactions.map(reaction => {
                return {
                    name: reaction.sampleName!,
                    primerNames: [
                        ...reaction.recentCustomPrimers,
                        ...reaction.shippedPrimers,
                        ...reaction.customPrimers,
                        ...reaction.standardPrimers,
                    ],
                };
            }),
            pickupCourierId: pickupCourierId,
            hasShipping: hasShipping,
        };
        return new Promise<HttpResponse<EstimatedAmount>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/estimate-amount`, body).subscribe({
                next: (res: EstimatedAmountDTO) => {
                    resolve(OrderSetupMapper.mapEstimatedAmountDTOtoModel(res));
                },
                error: async () => {
                    await this.router.navigateByUrl(AppRoutes.routes().ERROR);
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    addInformedUser(body: AddInformedUserPayload) {
        return new Promise<HttpResponse<{ id: number }>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/add-informed-user`, body).subscribe({
                next: (res: { id: number }) => {
                    resolve(OrderSetupMapper.mapAddInformedUserDTOtoModel(res));
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant(
                                'orders.setup.orderSummary.additionalContact.informed.addedSuccessfullyMessage'
                            )
                        )
                    );
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getDraftOrder(orderId: number) {
        return new Promise<HttpResponse<Order>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/get-draft/${orderId}`).subscribe({
                next: (res: DraftOrderDTO) => {
                    resolve(OrderSetupMapper.mapDraftOrderDTOtoModel(res));

                    if (res.warningMessage) {
                        this.messageService.add(
                            ToasterHelper.warning(this.translateService.instant(res.warningMessage))
                        );
                    } else {
                        this.messageService.add(
                            ToasterHelper.success(this.translateService.instant('order.setup.draftOrder.uploadMessage'))
                        );
                    }
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    downloadCsvTemplate(): Promise<HttpResponse<null>> {
        return new Promise<HttpResponse<null>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/download-csv-template`, {}, 'text').subscribe({
                next: res => {
                    const newBlob = new Blob([res], { type: 'text/csv' });
                    const data = window.URL.createObjectURL(newBlob);
                    const link = document.createElement('a');
                    link.href = data;
                    link.download = 'sequencing_template';
                    link.click();
                    resolve({ data: null, status: HttpStatusEnum.SUCCESS });
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    uploadExcelFile(file: FormData) {
        return new Promise<HttpResponse<ExcelOrderData>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/upload-csv`, file).subscribe({
                next: (res: ExcelOrderDTO) => {
                    this.messageService.add(
                        ToasterHelper.success(
                            this.translateService.instant('order.setup.uploadCsvFile.success', {
                                filename: (file.get('CsvFile') as File).name,
                            })
                        )
                    );
                    resolve(OrderSetupMapper.mapExcelOrderDTOtoModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    saveDraft(order: Order) {
        const body: SaveDraftPayload = OrderSetupMapper.mapOrderToSaveDraftPayload(order);
        return new Promise<HttpResponse<{ id: number }>>(resolve => {
            this.apiService.post(`${this.resourceUrl}/save-draft`, body).subscribe({
                next: (res: { id: number }) => {
                    resolve(OrderSetupMapper.mapSaveDraftDTOtoModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }

    getSearchedSamples(searchString: string): Promise<HttpResponse<StepTwoSampleData[]>> {
        return new Promise<HttpResponse<StepTwoSampleData[]>>(resolve => {
            this.apiService.get(`${this.resourceUrl}/search-samples`, { search: searchString }).subscribe({
                next: (res: StepTwoSampleDataDTO) => {
                    resolve(OrderSetupMapper.mapSampleDataDTOToModel(res));
                },
                error: () => {
                    resolve({ data: null, status: HttpStatusEnum.ERROR });
                },
            });
        });
    }
}
